import React, { useState, useRef } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import styles from './panel-layout.module.css';

const tabBarWrapper = cva(styles.tabBarWrapper);
const tabBarDropZone = cva(styles.tabBarDropZone);
const splitterWrapper = cva(styles.splitterWrapper);
const panelRoot = cva(styles.panelRoot);
const splitterPanel = cva(styles.splitterPanel);
import { DndContext, DragOverlay, PointerSensor, pointerWithin, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import type { DragEndEvent, DragMoveEvent, DragStartEvent } from '@dnd-kit/core';
import { Splitter, Flex } from '@nula/core-ui';
import { useSessionState } from '@/session';
import type { LayoutNode } from '@/session';
import { TabBar, TabDragGhost } from './tab-bar';
import type { DraggableTabData, DroppableTabBarData } from './tab-dnd';
import { computeDropIndex, resolveDragEnd } from './tab-dnd';

// ---------------------------------------------------------------------------
// buildSizeMap — maps panel IDs to their sizes from a Splitter onResize callback.
// Only panel-type children are mapped; split-type children are skipped (they
// manage their own sizes internally).
// ---------------------------------------------------------------------------

// Walk the layout tree to find the top-right panel:
// - horizontal split → recurse into the LAST child (rightmost)
// - vertical split → recurse into the FIRST child (topmost)
function topRightPanelId(node: LayoutNode): string | null {
  if (node.type === 'panel') return node.panelId;
  const child = node.direction === 'h'
    ? node.children[node.children.length - 1]
    : node.children[0];
  return child ? topRightPanelId(child) : null;
}

export function buildSizeMap(children: LayoutNode[], sizes: number[]): Record<string, number> {
  const result: Record<string, number> = {};
  children.forEach((child, i) => {
    if (child.type === 'panel' && sizes[i] !== undefined) {
      result[child.panelId] = sizes[i];
    }
  });
  return result;
}

// ---------------------------------------------------------------------------
// TabBarDropZone — wraps TabBar with a useDroppable target per panel
// ---------------------------------------------------------------------------

function TabBarDropZone({ panelId, children }: { panelId: string; children: React.ReactNode }) {
  const data: DroppableTabBarData = { type: 'tabbar', panelId };
  const { setNodeRef } = useDroppable({
    id: `tabbar-${panelId}`,
    data,
  });
  return (
    <Flex
      ref={setNodeRef}
      className={tabBarDropZone()}
    >
      {children}
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// PanelLayout
// ---------------------------------------------------------------------------

/**
 * Render-prop for the panel's content area. Injected by the app layer
 * (typically WorkspaceView) to keep PanelLayout decoupled from the
 * workspace route — layout/ must not import from app/ per the §8 zone rules.
 */
export type PanelContentRenderer = (args: {
  panelId: string;
  activeId: string;
  isFocused: boolean;
}) => React.ReactNode;

/**
 * PanelLayout renders the LayoutNode tree from SessionState using the Splitter
 * component from @nula/core-ui. No custom resize logic — Splitter handles all
 * drag-to-resize behaviour. DndContext is a no-op wrapper here; Session 4 will
 * wire useDraggable/useDroppable on tab items.
 */
export function PanelLayout({
  renderPanelContent,
  extraTabActions,
  onRenameDoc,
  onRenameCollection,
  onRenameConversation,
}: {
  renderPanelContent: PanelContentRenderer;
  extraTabActions?: React.ReactNode;
  onRenameDoc?: (id: string, title: string) => void;
  onRenameCollection?: (id: string, name: string) => void;
  onRenameConversation?: (id: string, title: string) => void;
}) {
  const { state, focusPanel, openPanel, closePanel, setPanelSizes, reorderTab, moveTab } = useSessionState();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [activeDrag, setActiveDrag] = useState<DraggableTabData | null>(null);
  // dragOver.dropIndex is in docTabs space (0-based, home excluded) — used for the spacer visual.
  // dragOverRef.current.dropIndex is in panel.tabs space (+1 for home) — used for reorderTab.
  const [dragOver, setDragOver] = useState<{ panelId: string; dropIndex: number } | null>(null);
  const dragOverRef = useRef<{ panelId: string; dropIndex: number } | null>(null);

  if (!state) return null;

  const handleDragStart = (event: DragStartEvent) => {
    const item = event.active.data.current as DraggableTabData | undefined;
    if (item?.type === 'tab') setActiveDrag(item);
  };

  const handleDragMove = (event: DragMoveEvent) => {
    const item = event.active.data.current as DraggableTabData | undefined;
    const zone = event.over?.data.current as DroppableTabBarData | undefined;
    if (item?.type !== 'tab' || !zone || zone.type !== 'tabbar') {
      dragOverRef.current = null;
      setDragOver(null);
      return;
    }
    const tabRects = Array.from(
      document.querySelectorAll<HTMLElement>(
        `[data-panel-id="${zone.panelId}"][data-draggable-type="tab"]`,
      ),
    )
      .filter((el) => el.dataset.tabId !== item.tabId)
      .map((el) => el.getBoundingClientRect());
    const dropIndex = computeDropIndex(
      (event.active.rect.current.translated ?? null) as DOMRect | null,
      (event.over?.rect ?? null) as DOMRect | null,
      tabRects,
    );
    const samePanelDrag = zone.panelId === item.fromPanelId;
    // displayIndex: position in full docTabs for the spacer visual.
    // dropIndex is in "remaining docTabs" space (dragged tab excluded).
    // When the insertion point is after the dragged tab's current slot,
    // one extra tab exists before that point in full docTabs, so add 1.
    const displayIndex = samePanelDrag && dropIndex > item.fromTabIndex
      ? dropIndex + 1
      : dropIndex;
    // tabsIndex: position in panel.tabs (after removal of dragged tab) for reorderTab.
    // panel.tabs has home at 0, so dropIndex in remaining-docTabs space = dropIndex + 1
    // in panel.tabs-after-removal space.
    const tabsIndex = dropIndex + 1;
    dragOverRef.current = { panelId: zone.panelId, dropIndex: tabsIndex };
    setDragOver({ panelId: zone.panelId, dropIndex: displayIndex });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const lastDragOver = dragOverRef.current;
    dragOverRef.current = null;
    setActiveDrag(null);
    setDragOver(null);
    const { active, over } = event;
    const item = active.data.current as DraggableTabData | undefined;
    const zone = over?.data.current as DroppableTabBarData | undefined;

    if (item?.type !== 'tab') return;

    // Use the index that was last shown by the spacer — it was computed with
    // the same filtering and index-space conversion as handleDragMove, so the
    // drop lands exactly where the gap appeared.
    const newIndex = lastDragOver && lastDragOver.panelId === zone?.panelId
      ? lastDragOver.dropIndex
      : computeDropIndex(
          (event.active.rect.current.translated ?? null) as DOMRect | null,
          (over?.rect ?? null) as DOMRect | null,
          Array.from(
            document.querySelectorAll<HTMLElement>(
              `[data-panel-id="${zone?.panelId}"][data-draggable-type="tab"]`,
            ),
          ).map((el) => el.getBoundingClientRect()),
        );
    resolveDragEnd(item, zone, newIndex, reorderTab, moveTab);
  };

  const panelCount = Object.keys(state.panels).length;
  const MAX_PANELS = 3;
  const canSplit = panelCount < MAX_PANELS;
  const actionsPanelId = topRightPanelId(state.layout);

  function renderNode(node: LayoutNode): React.ReactNode {
    if (node.type === 'panel') {
      const panel = state!.panels[node.panelId];
      if (!panel) return null;
      const isFocused = state!.focusedPanelId === node.panelId;

      return (
        <Splitter.Panel
          key={node.panelId}
          min={200}
          onClick={() => focusPanel(node.panelId)}
          data-focused={isFocused ? 'true' : 'false'}
          className={splitterPanel()}
        >
          <div className={clsx('nula-tabbar-wrapper', tabBarWrapper())}>
            <TabBarDropZone panelId={node.panelId}>
              <TabBar
                panelId={node.panelId}
                isFocused={isFocused}
                onSplit={canSplit ? (dir) => { focusPanel(node.panelId); openPanel(dir); } : undefined}
                onClosePanel={panelCount > 1 ? () => closePanel(node.panelId) : undefined}
                insertionIndex={dragOver?.panelId === node.panelId ? dragOver.dropIndex : undefined}
                extraActions={node.panelId === actionsPanelId ? extraTabActions : undefined}
                onRenameDoc={onRenameDoc}
                onRenameCollection={onRenameCollection}
                onRenameConversation={onRenameConversation}
              />
            </TabBarDropZone>
          </div>
          {renderPanelContent({
            panelId: node.panelId,
            activeId: panel.activeId,
            isFocused,
          })}
        </Splitter.Panel>
      );
    }

    // split node — renders a Splitter containing its children
    const splitterLayout = node.direction === 'h' ? 'horizontal' : 'vertical';

    return (
      <Splitter
        key={node.children.map((c) => c.type === 'panel' ? c.panelId : 'split').join('-')}
        layout={splitterLayout}
        onResize={(sizes) => setPanelSizes(buildSizeMap(node.children, sizes))}
        className={splitterWrapper()}
      >
        {node.children.map(renderNode)}
      </Splitter>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragMove={handleDragMove} onDragEnd={handleDragEnd}>
      {/* If root layout is a panel node, render without a wrapping Splitter.
          The single panel fills the available space directly. */}
      {state.layout.type === 'panel'
        ? (
          <Flex vertical className={panelRoot()}>
            {renderNode(state.layout)}
          </Flex>
        )
        : renderNode(state.layout)
      }
      <DragOverlay dropAnimation={null}>
        {activeDrag && (
          <TabDragGhost tabId={activeDrag.tabId} tabType={activeDrag.tabType} />
        )}
      </DragOverlay>
    </DndContext>
  );
}
